import { HttpClient, HttpParams, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, EMPTY, expand, map, reduce } from "rxjs";
import { environment } from "../../environments/environment";
import { StationClock } from "../models/station-clock.model";
import { AuthService } from "./auth.service";

@Injectable({ providedIn: "root" })
export class StationClockService {
  private base = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  // ✅ Same header logic as your other service
  private getHeaders(): HttpHeaders {
    const token = this.authService.getIdToken(); // or getAccessToken()

    let headers = new HttpHeaders({
      "Content-Type": "application/json"
    });

    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  getStationClocksByStation(
    stationId: number,
    page: number = 1,
    limit: number = 10,
    dayOfWeek?: number | null
  ): Observable<{
    data: StationClock[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  }> {
    let params = new HttpParams()
      .set("stationid", String(stationId))
      .set("page", String(page))
      .set("limit", String(limit));

    // When a specific day is requested the backend returns all clocks for that day (no pagination).
    if (dayOfWeek !== undefined && dayOfWeek !== null) {
      params = params.set("day_of_week", String(dayOfWeek));
    }

    return this.http
      .get<any>(`${this.base}/stationClocks`, {
        params,
        headers: this.getHeaders()
      })
      .pipe(
        map((res) => ({
          data: (res?.data as any[] | undefined) ?? [],
          totalCount: res?.totalCount ?? 0,
          currentPage: res?.currentPage ?? 1,
          pageSize: res?.pageSize ?? limit,
          totalPages: res?.totalPages ?? 1
        }))
      );
  }

  getStationClock(id: number): Observable<StationClock> {
    return this.http
      .get<any>(`${this.base}/stationClocks/${id}`, {
        headers: this.getHeaders()
      })
      .pipe(map((res) => res?.clock_entry as StationClock));
  }

  createStationClocks(
    breaks: Array<{
      stationid: number;
      break_length: number;
      day_of_week: number;
      break_start_time: string;
    }>
  ): Observable<any> {
    return this.http.post<any>(
      `${this.base}/stationClocks`,
      { breaks },
      { headers: this.getHeaders() }
    );
  }

  updateStationClock(
    id: number,
    patch: Partial<StationClock>
  ): Observable<StationClock> {
    return this.http
      .put<any>(`${this.base}/stationClocks/${id}`, patch, {
        headers: this.getHeaders()
      })
      .pipe(map((res) => res?.clock_entry as StationClock));
  }

  deleteStationClock(id: number): Observable<any> {
    return this.http.delete<any>(
      `${this.base}/stationClocks/${id}`,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Fetch every clock for a station by walking paginated GET pages.
   * Backend caps `limit` at 100, so a single request is not enough for large stations.
   */
  getAllStationClocksForStation(stationId: number): Observable<StationClock[]> {
    const pageSize = 100;
    return this.getStationClocksByStation(stationId, 1, pageSize).pipe(
      expand((page) => {
        if (page.currentPage >= page.totalPages) {
          return EMPTY;
        }
        return this.getStationClocksByStation(stationId, page.currentPage + 1, pageSize);
      }),
      reduce((all, page) => all.concat(page.data || []), [] as StationClock[]),
    );
  }

  /** Bulk delete clocks by id. Backend expects `{ clock_ids: number[] }`. */
  bulkDeleteClocks(clockIds: number[]): Observable<any> {
    return this.http.post<any>(
      `${this.base}/stationClocks/bulk-delete`,
      { clock_ids: clockIds },
      { headers: this.getHeaders() }
    );
  }

  /** Bulk update break minute (0-59). Backend expects `{ clock_ids, break_minute }`. */
  bulkUpdateBreakMinute(clockIds: number[], breakMinute: number): Observable<any> {
    return this.http.post<any>(
      `${this.base}/stationClocks/bulk-update`,
      { clock_ids: clockIds, break_minute: breakMinute },
      { headers: this.getHeaders() }
    );
  }

  /**
   * Grid-style bulk delete.
   * Backend expects `{ station_id, selections: [{ day, hour }] }` (day = 0-6).
   */
  bulkDeleteClocksGrid(
    stationId: number,
    selections: Array<{ day: number; hour: number }>
  ): Observable<any> {
    return this.http.post<any>(
      `${this.base}/stationClocks/bulk-delete-grid`,
      { station_id: stationId, selections },
      { headers: this.getHeaders() }
    );
  }
}